import React from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import ModalLayout from "../layouts/ModalLayout";
import { modalSecondaryButtonStyle } from "../styles/buttonStyles";

// pevne 2 desatinné miesta pre ceny v €
function formatEUR(v) {
  return `${Number(v).toFixed(2)} €`;
}

// variabilné čísla bez zbytočných núl pre zľavy/bonusy
function formatRate(v, decimals = 4) {
  return parseFloat(Number(v).toFixed(decimals)).toString();
}

function monthsToBreakEven(price, monthlyCap) {
  if (!monthlyCap || monthlyCap <= 0) return null;
  return parseFloat((price / monthlyCap).toFixed(2));
}

export default function SubscriptionInfoModal({
  visible,
  plan,
  onClose,
  userMembershipId,
  membershipValidTo,
  size = "medium"
}) {
  const { t } = useTranslation("membership_catalog");

  if (!visible || !plan) return null;

  const unit = plan.is_electric
    ? t("strings.UNIT_EUR_PER_KWH")
    : t("strings.UNIT_EUR_PER_L");

  const qtyUnit = plan.is_electric ? "kWh" : "l";
  const roi = monthsToBreakEven(plan.price, plan.cashback_limit);

  const translatedNetworkArr = t(
    plan.is_electric ? "network_bonus_electric" : "network_bonus_fuel",
    { returnObjects: true }
  );

  const networkArr =
    Array.isArray(plan.mlm_bonus_levels) &&
    plan.mlm_bonus_levels.length
      ? plan.mlm_bonus_levels
      : translatedNetworkArr;

  const getBonusPerUnit = (lvl) =>
    lvl?.bonus_per_unit ??
    (plan.is_electric ? lvl?.bonus_per_kwh : lvl?.bonus_per_l) ??
    0;

  const counts = {
    L1: networkArr?.[0]?.max_count ?? 0,
    L2: networkArr?.[1]?.max_count ?? 0,
    L3: networkArr?.[2]?.max_count ?? 0
  };

  const bonus = {
    L1: getBonusPerUnit(networkArr?.[0]),
    L2: getBonusPerUnit(networkArr?.[1]),
    L3: getBonusPerUnit(networkArr?.[2])
  };

  const totalL1 = counts.L1 * bonus.L1;
  const totalL2 = counts.L2 * bonus.L2;
  const totalL3 = counts.L3 * bonus.L3;

  const totalWithBase =
    plan.discount_per_unit +
    totalL1 +
    totalL2 +
    totalL3;

  const refLevels = t("referral_bonuses.levels", {
    returnObjects: true
  });

  const referralBonuses = {
    1: Number(plan.referral_bonus_l1 ?? 0),
    2: Number(plan.referral_bonus_l2 ?? 0),
    3: Number(plan.referral_bonus_l3 ?? 0)
  };

  return (
    <ModalLayout
      open={visible}
      onClose={onClose}
      size={size}
      zIndex={4000}
    >
      <h3 style={{ fontSize: 24, margin: 0, marginBottom: 20 }}>
        {t("strings.PLAN_INFO_TITLE")}
      </h3>

      <div style={{ overflowY: "auto", flex: 1 }}>
        <Typography
          sx={{ fontWeight: "bold", fontSize: 18, mb: 1 }}
        >
          {plan.type}
        </Typography>

        <Typography>
          {t("strings.PRICE_LABEL")}:{" "}
          {formatEUR(plan.price)}
        </Typography>

        <Typography>
          {t("strings.PER_MONTH_SHORT", {
            count: plan.duration_months
          })}
        </Typography>

        <Typography>
          {t("strings.DISCOUNT_PER_UNIT", {
            value: formatRate(plan.discount_per_unit),
            unit
          })}
        </Typography>

        {/* Cashback + ROI */}
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: "bold", mb: 1 }}>
            {t("strings.CASHBACK_AND_ROI_TITLE")}
          </Typography>

          <Typography>
            {plan.cashback_limit
              ? t("strings.CASHBACK_LIMIT", {
                  value: formatEUR(plan.cashback_limit)
                })
              : t("strings.CASHBACK_NO_LIMIT")}
          </Typography>

          <Typography>
            {t("strings.MAX_CASHBACK", {
              value: formatEUR(plan.max_cashback)
            })}
          </Typography>

          {plan.max_amount != null && (
            <Typography>
              {t("strings.MAX_AMOUNT", {
                value: plan.max_amount,
                unit: qtyUnit
              })}
            </Typography>
          )}

          {roi != null && (
            <Typography>
              {t("strings.ROI_MONTHS", {
                value: roi
              })}
            </Typography>
          )}
        </Box>

        {/* Active network bonus */}
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: "bold", mb: 1 }}>
            {t("strings.ACTIVE_NETWORK_BONUS_TITLE")}
          </Typography>

          <Typography sx={{ mb: 1 }}>
            {plan.is_electric
              ? t("strings.ACTIVE_NETWORK_BONUS_NOTE_ELECTRIC")
              : t("strings.ACTIVE_NETWORK_BONUS_NOTE_FUEL")}
          </Typography>

          {Array.isArray(networkArr) &&
            networkArr.map((lvl) => (
              <Typography key={lvl.level}>
                {t("strings.LEVEL")} {lvl.level} –{" "}
                {t("strings.MAX_COUNT")}: {lvl.max_count},{" "}
                {t("strings.BONUS_PER_UNIT")}:{" "}
                {formatRate(getBonusPerUnit(lvl))}{" "}
                {unit}
              </Typography>
            ))}
        </Box>

        {/* Network bonuses calculation */}
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: "bold", mb: 1 }}>
            {t("strings.NETWORK_BONUSES_TITLE")}
          </Typography>

          <Typography>
            1. {counts.L1} × {formatRate(bonus.L1)}{" "}
            {unit} = {formatRate(totalL1)} {unit}
          </Typography>

          <Typography>
            2. {counts.L2} × {formatRate(bonus.L2)}{" "}
            {unit} = {formatRate(totalL2)} {unit}
          </Typography>

          <Typography>
            3. {counts.L3} × {formatRate(bonus.L3)}{" "}
            {unit} = {formatRate(totalL3)} {unit}
          </Typography>

          <Typography sx={{ mt: 1, fontWeight: "bold" }}>
            {t("strings.TOTAL_MAX_DISCOUNT")}:{" "}
            {formatRate(totalWithBase)} {unit}
          </Typography>

          <Typography sx={{ mt: 1 }}>
            • {t("strings.BONUS_COND_1")}
          </Typography>

          <Typography>
            • {t("strings.BONUS_COND_2")}
          </Typography>

          <Typography>
            • {t("strings.BONUS_COND_3")}
          </Typography>
        </Box>

        {/* Referral bonuses */}
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: "bold", mb: 1 }}>
            {t("strings.REFERRAL_BONUSES_TITLE")}
          </Typography>

          <Typography sx={{ mb: 1 }}>
            {t("strings.REFERRAL_BONUS_NOTE")}
          </Typography>

          {Array.isArray(refLevels) &&
            refLevels.map((lvl) => (
              <Typography key={lvl.level}>
                {lvl.level}:{" "}
                {formatEUR(
                  referralBonuses[lvl.level] ?? 0
                )}{" "}
                – {lvl.condition}
              </Typography>
            ))}

          <Typography sx={{ mt: 1 }}>
            {t("referral_bonuses.payout_note")}
          </Typography>
        </Box>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: 16
        }}
      >
        <button
          style={modalSecondaryButtonStyle(
            null,
            "close-info",
            false
          )}
          onClick={onClose}
        >
          {t("strings.CLOSE")}
        </button>
      </div>
    </ModalLayout>
  );
}